import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useDebounce } from 'hooks/useDebounce';
import useGetFavoriteDocuments from 'domain/documents/hooks/useGetFavoriteDocuments';
import useDocumentSearch, { MIN_SEARCH_LENGTH } from 'domain/documents/hooks/useDocumentSearch';

const SUGGESTION_LIMIT = 5;

type UseSearchLogicOptions = {
  selectedTab?: number;
  navigateOnCommit?: boolean;
  pageSize?: number;
  fetchMainResults?: boolean;
};

export function useSearch({
  selectedTab,
  navigateOnCommit = false,
  pageSize = 10,
  fetchMainResults = true,
}: UseSearchLogicOptions = {}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialQueryFromUrl = searchParams.get('q') ?? '';
  const [draftQuery, setDraftQuery] = useState(initialQueryFromUrl);
  const [committedQuery, setCommittedQuery] = useState(initialQueryFromUrl);

  const debouncedDraftQuery = useDebounce(draftQuery, 250);

  const { favorites: favoriteDocuments, isLoading: isFavoriteDocumentsLoading } = useGetFavoriteDocuments();

  const formsSearch = useDocumentSearch(committedQuery, {
    pageSize,
    datasource: 'Forms',
    enabled: fetchMainResults,
  });
  const siteSearch = useDocumentSearch(committedQuery, {
    pageSize,
    datasource: 'Site',
    enabled: fetchMainResults,
  });

  const trimmedDraftQuery = draftQuery.trim();
  const trimmedCommittedQuery = committedQuery.trim();
  const isDraftValid = trimmedDraftQuery.length >= MIN_SEARCH_LENGTH;
  const isSameQuery = trimmedDraftQuery === trimmedCommittedQuery;

  // Tant que la saisie correspond à la recherche déjà lancée, les suggestions se servent dans ses
  // résultats : react-query les tient en cache, aucune requête supplémentaire n'est posée.
  const reusesMainResults = fetchMainResults && isSameQuery;

  const suggestionFormsSearch = useDocumentSearch(debouncedDraftQuery, {
    pageSize: SUGGESTION_LIMIT,
    datasource: 'Forms',
    enabled: isDraftValid && !reusesMainResults,
  });
  const suggestionSiteSearch = useDocumentSearch(debouncedDraftQuery, {
    pageSize: SUGGESTION_LIMIT,
    datasource: 'Site',
    enabled: isDraftValid && !reusesMainResults,
  });

  const suggestions = useMemo(() => {
    if (!isDraftValid) return [];

    if (reusesMainResults && selectedTab !== undefined) {
      const tabResults = selectedTab === 0 ? formsSearch.results : siteSearch.results;
      return tabResults.slice(0, SUGGESTION_LIMIT);
    }

    return [...suggestionFormsSearch.results, ...suggestionSiteSearch.results].slice(0, SUGGESTION_LIMIT);
  }, [
    isDraftValid,
    reusesMainResults,
    selectedTab,
    formsSearch.results,
    siteSearch.results,
    suggestionFormsSearch.results,
    suggestionSiteSearch.results,
  ]);

  const isSuggestionsLoading =
    reusesMainResults && selectedTab !== undefined
      ? selectedTab === 0
        ? formsSearch.isLoading
        : siteSearch.isLoading
      : suggestionFormsSearch.isLoading || suggestionSiteSearch.isLoading;

  const commitQuery = (nextQuery: string) => {
    const trimmed = nextQuery.trim();
    setCommittedQuery(trimmed);
    setSearchParams(trimmed ? { q: trimmed } : {});
    if (navigateOnCommit) {
      navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    }
  };

  useEffect(() => {
    const q = searchParams.get('q') ?? '';
    setDraftQuery(q);
    setCommittedQuery(q);
  }, [searchParams]);

  return {
    MIN_SEARCH_LENGTH,
    draftQuery,
    setDraftQuery,
    committedQuery,
    commitQuery,
    isCommittedValid: trimmedCommittedQuery.length >= MIN_SEARCH_LENGTH,
    suggestions,
    isSuggestionsLoading,
    favoriteDocuments,
    isFavoriteDocumentsLoading,
    formsSearch,
    siteSearch,
  };
}
